import { useEffect, useState } from 'react';
import { TrendingDown, TrendingUp, Calendar, Receipt } from 'lucide-react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';
import { useVertical } from '../../../context/VerticalContext';
import { supabase } from '../../../lib/supabase';

// One trend point per month. Category keys (plus "Other") are dynamic — the RPC
// picks the top 4 categories over the window and zero-fills every month so the
// stack order is stable.
interface TrendPoint {
    key: string;
    label: string;
    total: number;
    [category: string]: number | string;
}

// Shape returned by fn_get_expense_overview. Every amount is already costed
// server-side (gross for a non-VAT-registered / pre-registration purchase, net
// for a registered one) — the frontend only formats.
interface Overview {
    as_of: string;
    is_vat_registered: boolean;
    last_30_total: number;
    prior_30_total: number;
    delta_30_pct: number | null;
    top_category: { name: string; amount: number } | null;
    three_month_total: number;
    three_month_months_with_data: number;
    ytd_total: number;
    ytd_start: string;
    trend_top_categories: string[];
    trend: TrendPoint[];
}

const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n);

// Renders "6 Apr–5 Apr 2027" from the tax-year start the RPC computed, so the
// label always matches the boundary the figures were bucketed against.
const taxYearLabel = (startIso: string): string => {
    const start = new Date(startIso);
    const end = new Date(start.getFullYear() + 1, start.getMonth(), start.getDate() - 1);
    return `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}–${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

// Slice colours for the stacked trend bar. Top HMRC-aligned categories get
// distinct hues; everything else collapses into "Other" in muted grey.
const CATEGORY_PALETTE: Record<string, string> = {
    'Motor Parts & Consumables': 'var(--fd-chart-1)',
    'Workshop Rent & Rates':     'var(--fd-chart-2)',
    'Fuel & Vehicle Running Costs': 'var(--fd-chart-3)',
    'Diagnostic & Software Subs': 'var(--fd-chart-4)',
    'Subcontractor Labor':       'var(--fd-chart-5)',
    'Payment Processing Fees':   'var(--fd-chart-7)',
    'Advertising & Marketing':   'var(--fd-chart-8)',
    'Accountancy & Legal':       'var(--fd-chart-6)',
    Other:                       'var(--fd-text-muted)',
};

const colorFor = (cat: string) => CATEGORY_PALETTE[cat] ?? CATEGORY_PALETTE.Other;

export function ExpensesOverview() {
    const { activeOrgId } = useVertical();
    const [overview, setOverview] = useState<Overview | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!activeOrgId) return;

        const fetchOverview = async () => {
            setLoading(true);
            // All gross-vs-net / registration / date logic lives in the RPC —
            // the frontend just formats the numbers it returns.
            const { data, error } = await supabase.rpc('fn_get_expense_overview', {
                p_org_id: activeOrgId,
            });

            if (error) {
                console.error('ExpensesOverview fetch failed', error);
                setOverview(null);
            } else {
                setOverview(data as unknown as Overview);
            }
            setLoading(false);
        };

        fetchOverview();
    }, [activeOrgId]);

    if (loading) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-24 rounded-2xl bg-card border border-line animate-pulse" />
                ))}
            </div>
        );
    }

    const last30 = Number(overview?.last_30_total ?? 0);
    const delta = overview?.delta_30_pct ?? null;
    const deltaUp = delta !== null && delta >= 0;

    const monthsWithData = overview?.three_month_months_with_data ?? 0;
    const avg = monthsWithData > 0 ? Number(overview?.three_month_total ?? 0) / monthsWithData : 0;

    const ytd = Number(overview?.ytd_total ?? 0);
    const ytdRange = overview?.ytd_start ? taxYearLabel(overview.ytd_start) : '';

    const topCat = overview?.top_category ?? null;
    const trend = overview?.trend ?? [];
    const trendCats = overview?.trend_top_categories ?? [];

    // The RPC now starts the series at the org's first month of activity, so the
    // number of bars grows from 1 up to the 6-month cap. Title + subtitle track
    // the real span instead of always claiming "6-month".
    const monthsShown = trend.length;
    const hasSpend = trend.some((p) => Number(p.total ?? 0) > 0);
    const trendTitle =
        monthsShown <= 1 ? 'This month' : `${monthsShown}-month trend`;
    const trendSubtitle = trend.length
        ? `${trend[0].label}–${trend[trend.length - 1].label}`
        : 'No activity yet';

    return (
        <div className="space-y-6">
            {/* KPI strip — at-a-glance health */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiTile
                    label="Last 30 days"
                    value={formatCurrency(last30)}
                    foot={
                        delta === null ? (
                            <span className="text-fg-4">No prior period</span>
                        ) : (
                            <span className={deltaUp ? 'text-danger' : 'text-success'}>
                                {deltaUp ? <TrendingUp className="inline w-3 h-3 mr-1" /> : <TrendingDown className="inline w-3 h-3 mr-1" />}
                                {deltaUp ? '+' : ''}{delta.toFixed(0)}% vs prior 30 days
                            </span>
                        )
                    }
                    icon={Receipt}
                />
                <KpiTile
                    label={monthsWithData <= 1 ? 'Monthly avg' : `${monthsWithData}-month avg`}
                    value={formatCurrency(avg)}
                    foot={
                        <span className="text-fg-4">
                            {monthsWithData <= 1 ? 'One month of data' : `Avg over ${monthsWithData} months`}
                        </span>
                    }
                    icon={Calendar}
                />
                <KpiTile
                    label={`YTD spend · ${ytdRange}`}
                    value={formatCurrency(ytd)}
                    foot={<span className="text-fg-4">Since 6 April</span>}
                    icon={Calendar}
                />
                <KpiTile
                    label="Top category"
                    value={topCat?.name ?? '—'}
                    foot={
                        <span className="text-fg-4 font-mono tabular-nums">
                            {topCat ? formatCurrency(Number(topCat.amount)) : '£0'} · last 30 days
                        </span>
                    }
                    icon={TrendingUp}
                />
            </div>

            {/* Spend trend, stacked by category — starts at first month of activity */}
            <div className="rounded-2xl bg-raised border border-line p-5">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-fd-sm-tight uppercase tracking-[0.2em] text-fg-3 font-display font-semibold">
                        {trendTitle}
                    </h3>
                    <span className="text-fd-3xs font-mono tabular-nums text-fg-4">
                        {hasSpend ? `${trendSubtitle} · stacked by category` : trendSubtitle}
                    </span>
                </div>
                <div className="h-[220px]">
                    {hasSpend ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={trend} margin={{ top: 6, right: 6, left: 0, bottom: 0 }} barCategoryGap="28%">
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--fd-border)" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 11, fill: 'var(--fd-text-soft)' }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: 'var(--fd-text-soft)' }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v: number) => v >= 1000 ? `£${(v / 1000).toFixed(0)}k` : `£${v}`}
                                    width={45}
                                />
                                <Tooltip
                                    formatter={(value) => formatCurrency(Number(value ?? 0))}
                                    contentStyle={{
                                        background: 'var(--fd-surface)',
                                        border: '1px solid var(--fd-glass-border)',
                                        borderRadius: '0.5rem',
                                        fontSize: '12px',
                                    }}
                                    cursor={{ fill: 'color-mix(in oklab, var(--fd-tint) 4%, transparent)' }}
                                />
                                {trendCats.map((c) => (
                                    <Bar key={c} dataKey={c} stackId="a" fill={colorFor(c)} maxBarSize={72} />
                                ))}
                                <Bar dataKey="Other" stackId="a" fill={colorFor('Other')} maxBarSize={72} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center gap-1">
                            <Receipt className="w-5 h-5 text-fg-4" />
                            <p className="text-sm text-fg-3">No spend recorded yet</p>
                            <p className="text-fd-sm-tight text-fg-4">
                                Your monthly trend appears here once expenses are logged.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function KpiTile({
    label,
    value,
    foot,
    icon: Icon,
}: {
    label: string;
    value: string;
    foot: React.ReactNode;
    icon: React.ElementType;
}) {
    return (
        <div className="rounded-2xl bg-raised border border-line p-4">
            <div className="flex items-start justify-between mb-2">
                <span className="text-fd-3xs uppercase tracking-[0.18em] text-fg-3 font-display font-semibold leading-snug">
                    {label}
                </span>
                <Icon className="w-3.5 h-3.5 text-fg-4 shrink-0 mt-0.5" />
            </div>
            <div className="font-mono tabular-nums text-fg text-xl md:text-2xl font-semibold tracking-tight truncate">
                {value}
            </div>
            <div className="text-fd-sm-tight mt-1 font-mono tabular-nums">
                {foot}
            </div>
        </div>
    );
}
